/**
 * Team Evaluations Page
 *
 * Full page view of all evaluations for the manager's team.
 * Accessible via /team/beoordelingen
 */

import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth';
import { useEvaluations, useCreateEvaluation, useDeleteEvaluation } from '../evaluations/useEvaluations';
import { useTeamMembers } from './useTeam';
import { ScoreDisplay } from '../evaluations/wizard/ScoreInput';
import type { EvaluationStatus, Evaluation } from '../../lib/evaluationsApi';

const STATUS_CONFIG: Record<EvaluationStatus, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Concept', color: 'text-yellow-800', bgColor: 'bg-yellow-100' },
  submitted: { label: 'Ingediend', color: 'text-blue-800', bgColor: 'bg-blue-100' },
  in_review: { label: 'In Review', color: 'text-purple-800', bgColor: 'bg-purple-100' },
  final: { label: 'Definitief', color: 'text-green-800', bgColor: 'bg-green-100' },
  cancelled: { label: 'Geannuleerd', color: 'text-gray-600', bgColor: 'bg-gray-100' },
};

// Extended evaluation type with employee info
interface EvaluationWithEmployee extends Evaluation {
  employeeName?: string;
}

export function TeamEvaluationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<EvaluationStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'status' | 'employee' | 'score'>('date');
  const [showNewEvalForm, setShowNewEvalForm] = useState(false);
  const [newEvalEmployeeId, setNewEvalEmployeeId] = useState('');
  const [newEvalPeriode, setNewEvalPeriode] = useState({
    start: new Date().getFullYear() + '-01-01',
    end: new Date().getFullYear() + '-12-31',
  });

  const { data: evaluations, isLoading: evalLoading, error } = useEvaluations({ managerId: user?.id });
  const { data: teamMembers, isLoading: teamLoading } = useTeamMembers();
  const createEvaluation = useCreateEvaluation();
  const deleteEvaluation = useDeleteEvaluation();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const isLoading = evalLoading || teamLoading;

  const handleCreateEvaluation = async () => {
    if (!newEvalEmployeeId) return;

    try {
      const result = await createEvaluation.mutateAsync({
        employeeId: parseInt(newEvalEmployeeId),
        periodeStart: newEvalPeriode.start,
        periodeEnd: newEvalPeriode.end,
      });
      setShowNewEvalForm(false);
      setNewEvalEmployeeId('');
      navigate(`/beoordeling/${result.id}/wizard`);
    } catch {
      // Error handled by mutation
    }
  };

  const handleDeleteEvaluation = async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteEvaluation.mutateAsync(deleteConfirmId);
      setDeleteConfirmId(null);
    } catch {
      // Error handled by mutation
    }
  };

  // Map employee IDs to names using team members data
  const employeeMap = useMemo(() => {
    const map = new Map<number, string>();
    teamMembers?.forEach((member) => {
      map.set(member.employeeId, member.name);
    });
    return map;
  }, [teamMembers]);

  // Add employee names to evaluations
  const allEvaluations: EvaluationWithEmployee[] = useMemo(() => {
    return (evaluations?.data || []).map((e) => ({
      ...e,
      employeeName: employeeMap.get(e.employeeId),
    }));
  }, [evaluations?.data, employeeMap]);

  // Filter by status
  const filteredEvaluations = statusFilter === 'all'
    ? allEvaluations
    : allEvaluations.filter((e) => e.status === statusFilter);

  // Apply sorting
  const sortedEvaluations = [...filteredEvaluations].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.periodeStart).getTime() - new Date(a.periodeStart).getTime();
    }
    if (sortBy === 'status') {
      const statusOrder = ['draft', 'submitted', 'in_review', 'final', 'cancelled'];
      return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    }
    if (sortBy === 'employee') {
      const nameA = a.employeeName || '';
      const nameB = b.employeeName || '';
      return nameA.localeCompare(nameB);
    }
    // sortBy === 'score'
    const scoreA = a.totalScore ? parseFloat(a.totalScore) : 0;
    const scoreB = b.totalScore ? parseFloat(b.totalScore) : 0;
    return scoreB - scoreA;
  });

  // Calculate statistics
  const stats = {
    total: allEvaluations.length,
    draft: allEvaluations.filter((e) => e.status === 'draft').length,
    submitted: allEvaluations.filter((e) => e.status === 'submitted').length,
    inReview: allEvaluations.filter((e) => e.status === 'in_review').length,
    final: allEvaluations.filter((e) => e.status === 'final').length,
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Beoordelingen laden...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600">Er is een fout opgetreden bij het laden van beoordelingen.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header with breadcrumb */}
        <div className="mb-6">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Link to="/team" className="hover:text-blue-600">Mijn Team</Link>
            <span>›</span>
            <span className="text-gray-900">Beoordelingen</span>
          </nav>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Team Beoordelingen</h1>
              <p className="mt-1 text-sm text-gray-500">
                Overzicht van alle beoordelingen voor je teamleden.
              </p>
            </div>
            <button
              onClick={() => setShowNewEvalForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nieuw
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Totaal</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
            <p className="text-3xl font-bold text-yellow-700">{stats.draft}</p>
            <p className="text-sm text-yellow-600">Concept</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-3xl font-bold text-blue-700">{stats.submitted}</p>
            <p className="text-sm text-blue-600">Ingediend</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
            <p className="text-3xl font-bold text-purple-700">{stats.inReview}</p>
            <p className="text-sm text-purple-600">In Review</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-3xl font-bold text-green-700">{stats.final}</p>
            <p className="text-sm text-green-600">Definitief</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="p-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as EvaluationStatus | 'all')}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Alle</option>
                <option value="draft">Concept</option>
                <option value="submitted">Ingediend</option>
                <option value="in_review">In Review</option>
                <option value="final">Definitief</option>
                <option value="cancelled">Geannuleerd</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Sorteer:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'status' | 'employee' | 'score')}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="date">Datum</option>
                <option value="status">Status</option>
                <option value="employee">Medewerker</option>
                <option value="score">Score</option>
              </select>
            </div>

            <div className="ml-auto text-sm text-gray-500">
              {sortedEvaluations.length} resultaten
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Medewerker
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Periode
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Score
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedEvaluations.map((evaluation) => (
                  <tr key={evaluation.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 text-xs font-medium">
                            {evaluation.employeeName?.charAt(0) || 'M'}
                          </span>
                        </div>
                        <div>
                          <Link
                            to={`/medewerkers/${evaluation.employeeId}`}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600"
                          >
                            {evaluation.employeeName || `Medewerker #${evaluation.employeeId}`}
                          </Link>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                      {' - '}
                      {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[evaluation.status].bgColor} ${STATUS_CONFIG[evaluation.status].color}`}>
                        {STATUS_CONFIG[evaluation.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {evaluation.totalScore ? (
                        <ScoreDisplay
                          score={Math.round(parseFloat(evaluation.totalScore))}
                          size="sm"
                        />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {evaluation.status !== 'final' && (
                          <button
                            onClick={() => setDeleteConfirmId(evaluation.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Beoordeling verwijderen"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => window.open(`/beoordeling/${evaluation.id}/print`, '_blank')}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Scoringsformulier afdrukken"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigate(`/beoordeling/${evaluation.id}/wizard`)}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Bekijken
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {sortedEvaluations.length === 0 && (
              <div className="p-12 text-center">
                <svg
                  className="w-12 h-12 text-gray-300 mx-auto mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-500">
                  Geen beoordelingen gevonden
                  {statusFilter !== 'all' && ' met deze filters'}.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* New Evaluation Modal */}
        {showNewEvalForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Nieuwe Beoordeling</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medewerker
                  </label>
                  <select
                    value={newEvalEmployeeId}
                    onChange={(e) => setNewEvalEmployeeId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecteer medewerker...</option>
                    {teamMembers?.map((member) => (
                      <option key={member.id} value={member.employeeId}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start periode
                    </label>
                    <input
                      type="date"
                      value={newEvalPeriode.start}
                      onChange={(e) => setNewEvalPeriode({ ...newEvalPeriode, start: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Eind periode
                    </label>
                    <input
                      type="date"
                      value={newEvalPeriode.end}
                      onChange={(e) => setNewEvalPeriode({ ...newEvalPeriode, end: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowNewEvalForm(false);
                    setNewEvalEmployeeId('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleCreateEvaluation}
                  disabled={!newEvalEmployeeId || createEvaluation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {createEvaluation.isPending ? 'Bezig...' : 'Aanmaken'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Beoordeling verwijderen</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Weet je zeker dat je deze beoordeling wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDeleteEvaluation}
                  disabled={deleteEvaluation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleteEvaluation.isPending ? 'Verwijderen...' : 'Verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
